import React from 'react';
import { Link } from 'react-router-dom';
import { IconArrowRight } from '../components/icons';
import SxAccordion, { type AccordionEntry } from '../components/x/SxAccordion';
import SxReveal from '../components/x/SxReveal';
import SxSection from '../components/x/SxSection';
import SxShowcase from '../components/x/SxShowcase';
import ProductMatrixConsole from '../components/x/demos/ProductMatrixConsole';
import QuQuGraphDemo from '../components/x/demos/QuQuGraphDemo';
import RankingDemo from '../components/x/demos/RankingDemo';
import SalesAgentDemo from '../components/x/demos/SalesAgentDemo';
import SubscriptionDemo from '../components/x/demos/SubscriptionDemo';
import WatchSalaryDemo from '../components/x/demos/WatchSalaryDemo';
import { FAQ_SECTIONS } from '../data/faq';
import { NEWS_ITEMS } from '../data/news';
import {
  getProductById,
  getProductDisplayName,
  products,
  STATUS_LABELS,
  type Product,
} from '../data/products';

/**
 * 首页落地页 —— 对齐参考站（x.ai/bot）的视觉体系与交互精度，
 * 但信息架构服务于"公司门户 + 五款产品矩阵"而非单一产品。
 *
 * 与参考站的三处有意偏离，以及偏离的理由：
 * 1. 参考站的「三档定价」在这里改为「接入方式」。五款产品的商业模式完全不同
 *    （App 免费下载 / Pro 会员 / SaaS 订阅 / 商务洽谈），强行统一成价目表会失真。
 * 2. 参考站的「8 张 Agent 角色卡」改为「适用场景网格」，内容取自各产品
 *    products.ts 里已有的真实 scenarios，不虚构角色。
 * 3. 参考站的「客户案例视频」改为「技术底座」段。目前没有可公开的客户视频素材，
 *    与其放占位视频，不如展示真实的工程能力叙述。
 *
 * 所有文案与数据均取自本仓库既有内容（products / news / faq），未复制参考站文案。
 */

const LATEST_NEWS = NEWS_ITEMS[0];

/** 首页统计。数字由 products 实时推导，产品线变动时自动同步 */
const STATS = [
  { value: String(products.length), label: '自研产品' },
  { value: String(products.filter((p) => p.category === 'toc').length), label: 'ToC 应用' },
  { value: String(products.filter((p) => p.category === 'tob').length), label: 'ToB 产品' },
];

/** 产品能力段的编排。demo 与产品一一对应，顺序把重构中的产品排在最后 */
const SHOWCASES: Array<{
  id: string;
  category: string;
  demo: React.ReactNode;
  /** products.ts 里无 features 的产品需要显式给出能力标签 */
  fallbackHighlights?: string[];
  ctaLabel?: string;
}> = [
  { id: 'ququ', category: 'TOC · 关系管理', demo: <QuQuGraphDemo />, ctaLabel: '了解蛐蛐 QuQu' },
  { id: 'salary-reward', category: 'TOC · 效率陪伴', demo: <WatchSalaryDemo />, ctaLabel: '了解薪资奖励官' },
  { id: 'sales-agent', category: 'TOB · 增长引擎', demo: <SalesAgentDemo />, ctaLabel: '了解销售智能体' },
  { id: 'ranking-miniprogram', category: 'TOB · 团队激励', demo: <RankingDemo />, ctaLabel: '了解排名小程序' },
  {
    id: 'dingwuyou',
    category: 'TOC · 订阅管理',
    demo: <SubscriptionDemo />,
    ctaLabel: '了解订无忧',
  },
];

const highlightsOf = (product: Product, fallback?: string[]) =>
  product.features?.map((f) => f.title) ?? fallback ?? [];

/** 适用场景。全部摘自各产品的真实 scenarios 字段，未虚构 */
const SCENARIOS = [
  { role: '职场关系维护', from: '蛐蛐 QuQu', desc: '系统维护亲友、同事与客户关系，让社交从凭记忆变成可复盘。' },
  { role: '销售关系经营', from: '蛐蛐 QuQu', desc: '销售与创业者长期经营关系网络，关键连接不再遗漏。' },
  { role: '打工人计薪', from: '薪资奖励官', desc: '佩戴 Apple Watch 抬腕即见今日收入，每一分钟都有反馈。' },
  { role: '目标激励', from: '薪资奖励官', desc: '用可视化进度把工资攒成愿望，适配固定与弹性工时。' },
  { role: '本地商家获客', from: '销售智能体', desc: '教培、美业、餐饮等本地中小企业主，人少也能持续曝光接线索。' },
  { role: '内容团队缺位', from: '销售智能体', desc: '想用短视频获客但没有内容团队的品牌，从选题到成片全链路托管。' },
  { role: '学员积分激励', from: '排名小程序', desc: '培训与课程机构的学员积分排名，形成竞赛感与线下激励闭环。' },
  { role: '团队绩效透明', from: '排名小程序', desc: '有明确 KPI 的销售、运营与渠道团队，用榜单透明化绩效。' },
];

/** 三条价值主张，沿用公司既有表述 */
const VALUES = [
  { title: '产品驱动', desc: '每一款产品都源于真实场景，持续迭代而非概念堆砌。' },
  { title: '用户价值', desc: 'ToC 关注生活效率与情绪价值，ToB 关注可量化的业务增长。' },
  { title: '长期主义', desc: '从首款 App 到企业智能化工具，构建可持续的产品矩阵。' },
];

/**
 * 接入方式（替代参考站的定价表）。
 * 每档的能力条目均对应产品真实交付形态；具体报价需商务沟通，故不标价格数字。
 */
const ACCESS_TIERS = [
  {
    name: '个人用户',
    meta: '免费下载 · 可选会员',
    desc: '直接在应用商店获取 ToC 产品，核心功能免费，增值能力按需订阅。',
    items: ['App Store / 应用市场下载', '数据本机存储 + iCloud 同步', '无账号体系，隐私优先', 'QuQu Pro 增值会员可选'],
    cta: { label: '查看 ToC 产品', to: '/products#toc' },
    featured: false,
  },
  {
    name: '企业内测',
    meta: '销售智能体 · 预约演示',
    desc: '公域到私域的完整增长闭环，按行业交付话术包与执行策略，内测阶段限量接入。',
    items: ['公域→私域全链路托管', '6+ 行业话术与合规审核', '抖音真机发布 · Edge Agent', '老板看板与 ROI 台账'],
    cta: { label: '预约产品演示', to: '/contact' },
    featured: true,
  },
  {
    name: '机构订阅',
    meta: '排名小程序 · 按规模',
    desc: '微信生态内即开即用的积分排名 SaaS，多租户架构按机构规模灵活扩展。',
    items: ['微信小程序即开即用', '个人榜 + 队伍榜双维度', '积分兑奖与库存管理', '多租户管理后台'],
    cta: { label: '了解合作方式', to: '/contact' },
    featured: false,
  },
];

/** 交付端点。链接指向站内详情页；外部商店链接以产品数据为准 */
const PLATFORMS = [
  { name: 'iOS', note: 'iPhone 原生应用' },
  { name: 'watchOS', note: 'Apple Watch 表盘与复杂功能' },
  { name: 'Android', note: '主流应用市场' },
  { name: '微信小程序', note: '免安装，扫码即用' },
  { name: '企业 SaaS', note: '云端看板 + Edge Agent' },
];

/** 把分类 FAQ 摊平成手风琴条目，分类名保留为右侧标注 */
const FAQ_ITEMS: AccordionEntry[] = FAQ_SECTIONS.flatMap((section) =>
  section.questions.map((q) => ({
    question: q.question,
    answer: q.answer,
    category: section.title,
  }))
);

const CompanyHome: React.FC = () => (
  <div className="sx-page">
    {/* ═══ 01 公告条：对齐参考站顶部的 beta 通告位 ═══ */}
    <div style={{ borderBottom: '1px solid var(--sx-line)', background: 'var(--sx-surface)' }}>
      <div className="sx-shell">
        <Link
          to={`/news#${LATEST_NEWS.slug}`}
          className="flex items-center justify-center gap-2.5 py-2.5 group"
          style={{ textDecoration: 'none' }}
        >
          <span className="sx-dot sx-dot-live" aria-hidden="true" />
          <span className="sx-mono sx-mono-accent">最新</span>
          <span className="text-xs md:text-sm text-white truncate">{LATEST_NEWS.title}</span>
          <span
            className="sx-mono hidden sm:inline-flex items-center gap-1 transition-colors duration-300 group-hover:text-white"
            style={{ letterSpacing: '0.06em' }}
          >
            阅读
            <IconArrowRight size={11} />
          </span>
        </Link>
      </div>
    </div>

    {/* ═══ 02 Hero ═══ */}
    <section className="relative overflow-hidden">
      {/* 网格底纹是全页唯一的装饰层，向边缘径向淡出，不形成硬边界 */}
      <div className="sx-grid-bg" aria-hidden="true" />

      <div className="sx-shell relative">
        <div className="pt-16 pb-14 md:pt-28 md:pb-20">
          <SxReveal>
            <p className="sx-mono mb-6">三米云（上海）科技有限公司 · 成立于 2025</p>
          </SxReveal>

          <SxReveal delay={0.06}>
            <h1 className="sx-display max-w-4xl">
              自研产品，
              <br />
              连接生活与增长
            </h1>
          </SxReveal>

          <SxReveal delay={0.12}>
            {/* 文案写成一行：JSX 文本节点里的换行会被折叠成一个半角空格，
                中文之间多出这个空格在首屏大字号下清晰可见（「抬腕计薪， 到销售」） */}
            <p className="sx-body-lg mt-7" style={{ maxWidth: '56ch' }}>
              五款自研产品，覆盖个人生活工具与企业增长引擎。从关系管家、抬腕计薪，到销售智能体与团队排名——全部由我们自己定义、开发与迭代。
            </p>
          </SxReveal>

          <SxReveal delay={0.18}>
            <div className="flex flex-col sm:flex-row gap-3 mt-10">
              <Link to="/products" className="sx-pill sx-pill-primary group">
                探索产品
                <IconArrowRight
                  size={14}
                  className="transition-transform duration-300 group-hover:translate-x-0.5"
                />
              </Link>
              <Link to="/contact" className="sx-pill sx-pill-ghost">
                企业合作
              </Link>
            </div>
          </SxReveal>

          <SxReveal delay={0.24}>
            <dl
              className="grid grid-cols-3 gap-6 mt-14 pt-8 max-w-md"
              style={{ borderTop: '1px solid var(--sx-line)' }}
            >
              {STATS.map((s) => (
                <div key={s.label}>
                  <dt className="sx-num text-2xl md:text-3xl font-medium text-white leading-none">
                    {s.value}
                  </dt>
                  <dd className="sx-mono mt-2">{s.label}</dd>
                </div>
              ))}
            </dl>
          </SxReveal>
        </div>

        {/* Hero 演示区：产品矩阵实时状态 */}
        <SxReveal delay={0.3} className="pb-16 md:pb-24">
          <ProductMatrixConsole />
        </SxReveal>
      </div>
    </section>

    {/* ═══ 03 跑马灯：产品标签横向滚动 ═══ */}
    <div
      className="sx-marquee-mask py-5"
      style={{ borderTop: '1px solid var(--sx-line)', borderBottom: '1px solid var(--sx-line)' }}
    >
      <div className="sx-marquee" aria-hidden="true">
        {/* 内容重复两遍，配合 translateX(-50%) 实现无缝循环 */}
        {[0, 1].map((dup) => (
          <div key={dup} className="flex items-center shrink-0">
            {products.map((p) => (
              <span key={`${dup}-${p.id}`} className="flex items-center gap-3 px-6 shrink-0">
                <span
                  className="w-[5px] h-[5px] rounded-full"
                  style={{ background: 'var(--sx-accent)' }}
                />
                <span className="text-sm font-medium text-white whitespace-nowrap">
                  {getProductDisplayName(p)}
                </span>
                <span className="sx-mono whitespace-nowrap">{p.tagline}</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>

    {/* ═══ 04–08 五款产品能力演示，左右交替 ═══ */}
    {SHOWCASES.map((s, i) => {
      const product = getProductById(s.id);
      if (!product) return null;
      // 已上线是默认状态无需标注，内测/重构中必须让访客看见
      const statusLabel = product.status === 'live' ? undefined : STATUS_LABELS[product.status];
      return (
        <SxShowcase
          key={s.id}
          index={String(i + 1).padStart(2, '0')}
          category={s.category}
          title={getProductDisplayName(product)}
          tagline={product.tagline}
          description={product.description}
          highlights={highlightsOf(product, s.fallbackHighlights)}
          href={product.route}
          ctaLabel={s.ctaLabel}
          statusLabel={statusLabel}
          reverse={i % 2 === 1}
          demo={s.demo}
        />
      );
    })}

    {/* ═══ 09 适用场景网格（对应参考站的角色卡网格）═══ */}
    <SxSection
      bordered
      eyebrow="USE CASES · 适用场景"
      title="谁在使用这些产品"
      description="场景全部来自产品定义阶段确认的真实用户画像，而非泛化的行业清单。"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {SCENARIOS.map((s, i) => (
          <SxReveal key={s.role} delay={(i % 4) * 0.05}>
            <div className="sx-card sx-card-hover h-full p-5">
              <p className="sx-mono sx-mono-plain mb-3">{s.from}</p>
              <h3 className="sx-h3 mb-2.5">{s.role}</h3>
              <p className="sx-small">{s.desc}</p>
            </div>
          </SxReveal>
        ))}
      </div>
    </SxSection>

    {/* ═══ 10 技术底座（替代参考站的客户案例视频位）═══ */}
    <SxSection bordered eyebrow="FOUNDATION · 技术底座" title="从移动端到云端的工程能力">
      <SxReveal>
        <div className="sx-card overflow-hidden">
          {/*
            原先是一张 3D 电路板渲染图 + 三段式压暗遮罩。图是生成式渲染，
            且必须靠遮罩压到接近纯黑才能让文字可读 —— 既然如此，图就没在承担信息，
            只是在增加一次首屏请求。换成 hairline 网格后文案原样保留。
          */}
          <div className="relative" style={{ minHeight: 'min(46vw, 340px)' }}>
            <div className="sx-grid-bg" aria-hidden="true" />
            <div className="absolute inset-0 flex items-center">
              <div className="px-6 md:px-12 max-w-xl">
                <p className="sx-mono mb-4">END TO END</p>
                <h3 className="sx-h2 mb-4">端到端交付</h3>
                <p className="sx-body">
                  从移动端到云端，从 AI 推理到业务闭环——我们用工程能力把产品想法变成可交付的系统。
                </p>
              </div>
            </div>
          </div>
        </div>
      </SxReveal>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
        {VALUES.map((v, i) => (
          <SxReveal key={v.title} delay={i * 0.06}>
            <div className="sx-card h-full p-6 md:p-7">
              <p className="sx-num text-xs mb-5" style={{ color: 'var(--sx-mute)' }}>
                {String(i + 1).padStart(2, '0')}
              </p>
              <h3 className="sx-h3 mb-3">{v.title}</h3>
              <p className="sx-small">{v.desc}</p>
            </div>
          </SxReveal>
        ))}
      </div>
    </SxSection>

    {/* ═══ 11 接入方式（替代参考站的定价表）═══ */}
    <SxSection
      bordered
      eyebrow="ACCESS · 接入方式"
      title="按你的身份选择接入路径"
      description="五款产品的交付形态各不相同，因此这里给出的是接入方式而非统一价目表。具体报价随规模与场景确定。"
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {ACCESS_TIERS.map((tier, i) => (
          <SxReveal key={tier.name} delay={i * 0.07}>
            <div
              className="sx-card h-full flex flex-col p-6 md:p-7"
              style={
                tier.featured
                  ? { borderColor: 'rgba(255,99,8,0.35)', background: 'var(--sx-surface-2)' }
                  : undefined
              }
            >
              <div className="flex items-center justify-between gap-3 mb-5">
                <h3 className="sx-h3">{tier.name}</h3>
                {tier.featured && <span className="sx-mono sx-mono-accent">内测限量</span>}
              </div>

              <p className="sx-mono sx-mono-plain mb-4">{tier.meta}</p>
              <p className="sx-small mb-6">{tier.desc}</p>

              <ul className="space-y-2.5 mb-8 flex-1">
                {tier.items.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <span
                      className="mt-[7px] w-1 h-1 rounded-full shrink-0"
                      style={{ background: tier.featured ? 'var(--sx-accent)' : 'var(--sx-mute)' }}
                      aria-hidden="true"
                    />
                    <span className="text-sm" style={{ color: 'var(--sx-dim)' }}>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>

              <Link
                to={tier.cta.to}
                className={`sx-pill w-full ${tier.featured ? 'sx-pill-accent' : 'sx-pill-ghost'}`}
              >
                {tier.cta.label}
              </Link>
            </div>
          </SxReveal>
        ))}
      </div>
    </SxSection>

    {/* ═══ 12 多端获取（替代参考站的下载区）═══ */}
    <SxSection
      bordered
      eyebrow="PLATFORMS · 交付端点"
      title="在你需要的端上运行"
      description="不同产品对应不同的交付形态，各端能力以产品详情页说明为准。"
    >
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {PLATFORMS.map((p, i) => (
          <SxReveal key={p.name} delay={i * 0.04}>
            <div className="sx-card sx-card-hover h-full p-5">
              <p className="text-sm font-medium text-white mb-2">{p.name}</p>
              <p className="sx-small text-xs">{p.note}</p>
            </div>
          </SxReveal>
        ))}
      </div>

      <SxReveal delay={0.2}>
        <div className="flex flex-col sm:flex-row gap-3 mt-10">
          <Link to="/products" className="sx-pill sx-pill-primary group">
            查看全部产品
            <IconArrowRight
              size={14}
              className="transition-transform duration-300 group-hover:translate-x-0.5"
            />
          </Link>
          <Link to="/news" className="sx-pill sx-pill-ghost">
            产品动态
          </Link>
        </div>
      </SxReveal>
    </SxSection>

    {/* ═══ 13 FAQ ═══ */}
    <SxSection bordered eyebrow="FAQ · 常见问题" title="常见问题">
      <div style={{ maxWidth: '860px' }}>
        <SxAccordion items={FAQ_ITEMS} />
      </div>
      <SxReveal>
        <p className="sx-small mt-8">
          没有找到答案？
          <Link
            to="/contact"
            className="ml-1.5 underline"
            style={{ color: 'var(--sx-accent)', textUnderlineOffset: '3px' }}
          >
            直接联系我们
          </Link>
        </p>
      </SxReveal>
    </SxSection>

    {/* ═══ 14 收尾 CTA ═══ */}
    <section className="sx-section sx-divide relative overflow-hidden">
      <div className="sx-grid-bg" aria-hidden="true" />
      <div className="sx-shell relative">
        <SxReveal className="text-center">
          <p className="sx-mono mb-5">GET IN TOUCH</p>
          <h2 className="sx-h2 mb-5">准备开始合作？</h2>
          <p className="sx-body-lg mx-auto mb-10" style={{ maxWidth: '46ch' }}>
            产品体验、企业定制或商务合作，欢迎直接联系我们。
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact" className="sx-pill sx-pill-primary group">
              联系我们
              <IconArrowRight
                size={14}
                className="transition-transform duration-300 group-hover:translate-x-0.5"
              />
            </Link>
            <Link to="/about" className="sx-pill sx-pill-ghost">
              关于三米云
            </Link>
          </div>
        </SxReveal>
      </div>
    </section>
  </div>
);

export default CompanyHome;
